import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from '@/lib/api';
import { toast } from "@/lib/toast-with-sound";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  User, Shield, LogOut, ArrowLeft, Warehouse, Users, 
  Home, Palette, Route, UserPlus, UserCheck, MessageCircle, Instagram, CloudSun, Mail, Smartphone, Map as MapIcon
} from "lucide-react";
import { WeeklyWeatherModal } from "@/components/WeeklyWeatherModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useMyClubsData } from "@/hooks/queries/useClubs";
import { useLimits } from "@/contexts/LimitsContext";
import { useActionBar } from "@/contexts/ActionBarContext";
import { ShareModal } from "./ShareModal";
import { NotificationBell } from "./NotificationBell";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { PlanBadgeIcon } from "./PlanBadgeAvatar";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { useConfig } from "@/contexts/ConfigContext";
import { useQueryClient } from "@tanstack/react-query";
import { BACKEND_URL } from "../config";
import { TripActionButtons } from "./TripActionButtons";
const MOTRI_AVATAR = "/images/motri-avatar-sm.png";

export function AppHeader({ showBack = false, backTo = "/", actionBarContent: propActionBarContent = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [weatherOpen, setWeatherOpen] = useState(false);
  
  const { actionBarContent: contextActionBarContent } = useActionBar();
  const actionBarContent = propActionBarContent || contextActionBarContent || <TripActionButtons />;
  const { user, isAuthenticated, logout } = useAuth();
  const { data: clubsData } = useMyClubsData();
  const pendingInvitationsCount = (isAuthenticated && clubsData?.invitations?.length) || 0;
  const { plan } = useLimits();
  const configCtx = useConfig();
  const tawkMode = configCtx?.features?.tawkMode || "hidden";
  const isContactPage = location.pathname === '/contactos';
  const showTawkButton = tawkMode === "all_pages" || (tawkMode === "contacts_only" && isContactPage);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      toast.error(t("header.logout_error"));
    }
  };

  const handleNavigate = (path) => {
    setMenuOpen(false);
    const currentBasePath = location.pathname;
    const isSamePath = currentBasePath === path || currentBasePath.startsWith(path + "/") || path.startsWith(currentBasePath);
    if (isSamePath) {
      if (path.includes("meus-passeios")) queryClient.invalidateQueries({ queryKey: ["my-trips"] });
      else if (path.includes("meus-clubes")) queryClient.invalidateQueries({ queryKey: ["my-clubs"] });
      else if (path.includes("minhas-colaboracoes")) queryClient.invalidateQueries({ queryKey: ["my-collaborations"] });
      else if (path.includes("minhas-participacoes")) queryClient.invalidateQueries({ queryKey: ["my-participations"] });
      window.location.href = path;
    } else {
      navigate(path);
    }
  };

  const handleProtectedNavigate = (path, message) => {
    setMenuOpen(false);
    if (isAuthenticated) {
      handleNavigate(path);
    } else {
      handleUnauthorizedAccess(navigate, { returnTo: path, message });
    }
  };

  // Public navigation items — visible to everyone
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const navItems = [
    { path: "/", label: t("header.community"), icon: Home, tooltip: t("header.tooltip_community") },
    { path: "/passeios", label: t("header.nav_trips", "Passeios"), icon: Route, tooltip: t("header.tooltip_trips") },
    { path: "/rotas", label: t("header.routes", "Rotas"), icon: MapIcon, tooltip: t("header.tooltip_routes", "Explorar rotas partilhadas pela comunidade") },
    { path: "/clubes", label: t("header.nav_clubs", "Clubes"), icon: Users, tooltip: t("header.tooltip_clubs") },
    // PT-only: MotoTrips Go beta landing page
    ...(currentLang === "pt"
      ? [{
          path: "/go",
          label: "Go (beta)",
          icon: Smartphone,
          tooltip: "Conhece a app Android MotoTrips Go (Beta interno)",
          highlight: true,
        }]
      : []),
  ];

  return (
    <header className="fixed left-0 right-0 z-[9999]" style={{ top: "calc(var(--lang-banner-h, 0px) + var(--beta-banner-h, 0px))" }}>
      <div className="border-b border-border glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-[auto_1fr] gap-x-3 md:flex items-center md:h-24">
            {/* Left — Logo + Back */}
            <div className="row-span-2 md:row-auto flex items-center gap-4 shrink-0">
              {showBack && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={() => navigate(backTo)} data-testid="back-btn">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        {t("header.back")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>{t("header.tooltip_back")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => navigate("/")}
                      className="flex items-center hover:opacity-80 transition-opacity md:mt-5 relative z-30"
                      data-testid="logo-btn"
                    >
                      <img
                        src="/logo-mototrips.png"
                        alt="MotoTrips"
                        className="h-24 md:h-28 w-auto object-contain"
                        fetchPriority="high"
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{t("header.tooltip_logo")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Center — Desktop nav (flex-1 centered in available space) */}
            <div className="hidden md:flex flex-1 justify-center">
              <nav className="flex items-center gap-1">
                <TooltipProvider delayDuration={100}>
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleNavigate(item.path)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
                              item.highlight
                                ? "text-orange-500 hover:bg-orange-500/10 hover:text-orange-400"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            }`}
                            data-testid={`nav-${item.path.replace('/', '')}`}
                          >
                            <Icon className="h-5 w-5" />
                            <span className="font-medium">{item.label}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          <p>{item.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </TooltipProvider>
              </nav>
            </div>

            {/* Right — utils + user */}
            <div className="flex items-center justify-end gap-2 shrink-0">
              {/* Instagram */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href="https://www.instagram.com/mototrips.insta/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      data-testid="header-instagram-link"
                      aria-label="Instagram MotoTrips"
                    >
                      <Instagram className="h-5 w-5" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{t("header.tooltip_instagram")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Share */}
              <ShareModal type="site" iconOnly variant="ghost" size="sm" />

              {/* Language */}
              <LanguageSwitcher variant="ghost" size="sm" />

              {/* Authenticated: Notifications + Avatar dropdown */}
              {isAuthenticated ? (
                <div className="flex items-center gap-1">
                  <NotificationBell />

                  <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center gap-2 p-0 ml-1 md:pl-4 md:pr-0 md:py-2" data-testid="user-menu-btn">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.picture} alt={user?.name} />
                          <AvatarFallback>{(user?.nickname || user?.name)?.charAt(0) || "U"}</AvatarFallback>
                        </Avatar>
                        <div className="hidden sm:flex flex-col items-end">
                          <span className="text-sm font-medium flex items-center gap-1">
                            {user?.nickname || user?.name}
                            <PlanBadgeIcon badge={plan?.badge} size="h-3 w-3" />
                          </span>
                          <span className={`text-[10px] uppercase tracking-wide ${
                            user?.role === 'owner' ? 'text-yellow-500' : 
                            user?.role === 'admin' ? 'text-primary' : 
                            'text-muted-foreground'
                          }`}>
                            {user?.role === 'owner' ? t("header.owner") : 
                             user?.role === 'admin' ? 'Admin' : 
                             t("header.member")}
                          </span>
                        </div>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 z-[10000] border-2 border-zinc-600 shadow-2xl shadow-black/50">
                      {/* User email */}
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        {user?.email}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />

                      {/* Section: Geral */}
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        {t("header.avatar_menu.general", "Geral")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleNavigate("/meu-canto/editar")} className="cursor-pointer" data-testid="avatar-my-corner">
                        <Home className="h-4 w-4 mr-2" />
                        {t("header.my_links_dropdown.my_corner")}
                      </DropdownMenuItem>

                      {/* Section: Rotas (formerly "MotoTrips Go") —
                          renamed 2026-05-09 to make room for non-Go
                          route tooling alongside the Go-app surfaces.
                          Visible to all authenticated users. */}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        {t("header.my_links_dropdown.routes_section", "Rotas")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleNavigate("/go")} className="cursor-pointer" data-testid="avatar-go-beta">
                        <Smartphone className="h-4 w-4 mr-2" />
                        Go (beta)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleNavigate("/gestaorotas")} className="cursor-pointer" data-testid="avatar-go-routes">
                        <MapIcon className="h-4 w-4 mr-2" />
                        {t("header.my_links_dropdown.route_management", "Gestão de Rotas")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleNavigate("/rotas/ferramentas")} className="cursor-pointer" data-testid="avatar-routes-tools">
                        <Route className="h-4 w-4 mr-2" />
                        {t("header.my_links_dropdown.route_editor", "Editor de Rotas")}
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      {/* Section: Passeios */}
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        {t("header.avatar_menu.my_clubs")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleNavigate("/meus-clubes")} className="cursor-pointer" data-testid="avatar-my-clubs">
                        <Users className="h-4 w-4 mr-2" />
                        {t("header.my_links_dropdown.my_clubs")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleNavigate("/minhas-adesoes")} className="cursor-pointer relative" data-testid="avatar-my-memberships">
                        <Mail className="h-4 w-4 mr-2" />
                        {t("header.my_links_dropdown.my_memberships")}
                        {pendingInvitationsCount > 0 && (
                          <span className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-destructive text-destructive-foreground">
                            {pendingInvitationsCount}
                          </span>
                        )}
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      {/* Section: Passeios */}
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        {t("header.avatar_menu.my_trips")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleNavigate("/meus-passeios")} className="cursor-pointer" data-testid="avatar-my-trips">
                        <Route className="h-4 w-4 mr-2" />
                        {t("header.data_menu.my_personal_trips")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleNavigate("/minhas-colaboracoes")} className="cursor-pointer" data-testid="avatar-collaborations">
                        <UserPlus className="h-4 w-4 mr-2" />
                        {t("header.data_menu.collaborations")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleNavigate("/meus-clubes/passeios")} className="cursor-pointer" data-testid="avatar-group-trips">
                        <Users className="h-4 w-4 mr-2" />
                        {t("header.data_menu.group_trips")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleNavigate("/minhas-participacoes")} className="cursor-pointer" data-testid="avatar-participations">
                        <UserCheck className="h-4 w-4 mr-2" />
                        {t("header.data_menu.participations")}
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      {/* Section: Conta */}
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        {t("header.avatar_menu.account")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleNavigate("/perfil")} className="cursor-pointer" data-testid="profile-btn">
                        <User className="h-4 w-4 mr-2" />
                        {t("header.my_profile")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleNavigate("/preferencias")} className="cursor-pointer" data-testid="preferences-btn">
                        <Palette className="h-4 w-4 mr-2" />
                        {t("header.preferences")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleNavigate("/embaixador")} className="cursor-pointer" data-testid="referrals-btn">
                        <UserPlus className="h-4 w-4 mr-2" />
                        {t("header.referrals")}
                      </DropdownMenuItem>
                      {user?.is_admin && (
                        <DropdownMenuItem onClick={() => handleNavigate("/admin")} className="cursor-pointer" data-testid="admin-btn">
                          <Shield className="h-4 w-4 mr-2" />
                          {t("header.admin")}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer text-destructive focus:text-destructive"
                        data-testid="logout-btn"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {t("header.logout")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                /* Anonymous — Login button */
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="flex items-center gap-2 p-0 ml-1" 
                        onClick={() => navigate("/login")}
                        data-testid="login-avatar-btn"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-muted">
                            <User className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="hidden sm:flex flex-col items-end">
                          <span className="text-sm font-medium">{t("header.visitor")}</span>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            {t("header.not_registered")}
                          </span>
                        </div>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>{t("header.tooltip_login")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Mobile navigation — Row 2, public items only */}
            <nav className="md:hidden col-start-2 flex items-start justify-end pb-1" style={{ marginRight: '-10px' }}>
              {navItems.map((item, idx) => {
                const Icon = item.icon;
                const isLast = idx === navItems.length - 1;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item.path)}
                    className={`flex flex-col items-center gap-0.5 py-1 w-14 rounded-lg transition-all ${
                      item.highlight
                        ? "text-orange-500 hover:bg-orange-500/10 hover:text-orange-400"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    } ${isLast ? 'pr-0' : ''}`}
                    data-testid={`nav-mobile-${item.path.replace('/', '')}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-[10px] leading-tight font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
      
      {/* Action Bar */}
      <div className="action-bar" data-testid="action-bar">
        <div className="action-bar-inner">
          <div className="action-bar-content">
            {actionBarContent}
          </div>
          {/* Tawk.to Chat — Mobile only, respects admin tawk_mode config */}
          {showTawkButton && (
            <button
              onClick={() => {
                if (window.Tawk_API && window.Tawk_API.maximize) {
                  try { window.Tawk_API.showWidget(); window.Tawk_API.maximize(); } catch {}
                }
              }}
              className="md:hidden relative h-9 w-9 flex items-center justify-center rounded-md border border-border bg-background/80 hover:bg-background hover:border-primary transition-colors shrink-0 ml-2"
              data-testid="tawk-mobile-btn"
            >
              <MessageCircle className="h-5 w-5 text-primary" />
            </button>
          )}
          {/* Weekly Weather */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => isAuthenticated ? setWeatherOpen(true) : navigate("/regista-te")}
                  className="relative h-9 w-9 flex items-center justify-center rounded-md border border-border bg-background/80 hover:bg-background hover:border-primary transition-colors shrink-0 ml-2"
                  data-testid="weather-nav-btn"
                >
                  <CloudSun className="h-5 w-5 text-primary" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">{t("common:weather_tooltip", "Tempo dos próximos 7 dias")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <WeeklyWeatherModal open={weatherOpen} onOpenChange={setWeatherOpen} />
          {/* Motri AI Assistant */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    if (!isAuthenticated) return navigate("/motri-apresentacao");
                    const hasMotriAccess = user?.role === "owner" || user?.role === "admin" || plan?.plan_slug === "road_master";
                    navigate(hasMotriAccess ? "/motri" : "/motri-apresentacao");
                  }}
                  className="relative h-9 w-9 flex items-center justify-center rounded-md border border-border bg-background/80 hover:bg-background hover:border-primary transition-colors shrink-0 ml-2 overflow-hidden"
                  data-testid="motri-nav-btn"
                >
                  <img src={MOTRI_AVATAR} alt="Motri" className="w-full h-full object-cover" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">{t("common:motri_tooltip", "Motri — Assistente de passeios IA")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
